import * as Immutable from 'immutable';

import TransporterActions from 'common/redux/actions/transporter';

import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import {
    MasterTransporter,
    TransporterTypeType
} from 'common/models/internal/MasterTransporter';

import { PhoneNumber } from 'common/models/internal/PhoneNumber';

import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import {
    transporter as transporterApi,
    cities as citiesApi,
    pincodes as pincodesApi
} from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import {
    EntityDocumentUploadList,
    EntityDocumentUploadCard
} from 'common/entity/documents';
import CountryHelper from 'common/helpers/CountryHelper';

import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { ENTITY_NAME_ID } from 'common/constants/entity/transporterMaster';

const formatCreateLabel = t => label => {
    return t('transporterForm.createPrompt', {
        code: label
    });
};

const TransporterTypeOptions = TransporterTypeType.getOptions({
    translationKey: 'masterTransporterProperties.types'
});

const TRANSPORTER_FIELDS = [
    {
        accessor: 'code',
        label: 'transporterForm.code',
        helpText: 'transporterForm.codeTooltip',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.getIn(['id']),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'transporterForm.name',
        placeholder: 'transporterForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'registeredName',
        label: 'transporterForm.registeredName',
        placeholder: 'transporterForm.companyNamePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'tagline',
        label: 'transporterForm.companyTag',
        placeholder: 'transporterForm.companyTagPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'type',
        type: FieldTypeEnum.Select,
        required: true,
        label: 'transporterForm.type',
        placeholder: 'transporterForm.typePlaceholder',
        options: TransporterTypeOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'logo',
        component: EntityDocumentUploadCard,
        label: 'transporterForm.logo',
        extraProps: ({ field, entity, t }) => {
            return {
                currentDocument: {
                    type: 'logo',
                    imageUrl: entity.getIn(field.absoluteAccessor)
                },
                accept: ['.png', '.jpg', '.jpeg'],
                t
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value);
        }
    },
    {
        accessor: 'isActive',
        required: true,
        label: 'transporterForm.isActive',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('transporterForm.boolean.yes')
                    : t('transporterForm.boolean.no')
            };
        }
    },
    {
        accessor: 'teams',
        label: 'transporterForm.teams',
        required: true,
        type: FieldTypeEnum.Teams,
        helpText: 'transporterForm.teamsTooltip'
    },
    {
        accessor: 'contact',
        type: FieldTypeEnum.Contact,
        extraProps: ({ entity, t, props }) => {
            const { validateDuplicateId } = props;
            return {
                validateDuplicateId,
                translationsObject: {
                    label: t('transporterForm.contact'),
                    helpText: t('transporterForm.contactTooltip'),
                    addContactText: t('transporterForm.addContact')
                },
                teamIds: entity.getIn(['teams'])
            };
        },
        onChangeHandler: ({ entity, value }) => {
            return entity.setContact(value);
        }
    },
    {
        accessor: 'address.placeName',
        supportsOnBlur: true,
        label: 'transporterForm.placeName',
        helpText: 'transporterForm.placeNameTooltip',
        placeholder: 'transporterForm.placeNamePlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.formattedAddress',
        supportsOnBlur: true,
        label: 'transporterForm.address',
        required: true,
        placeholder: 'transporterForm.addressPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.localityName',
        supportsOnBlur: true,
        label: 'transporterForm.locality',
        placeholder: 'transporterForm.localityPlaceholder',
        halfWidth: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.city',
        label: 'transporterForm.city',
        placeholder: 'transporterForm.cityPlaceholder',
        supportsOnBlur: true,
        type: FieldTypeEnum.City,
        halfWidth: true,
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        extraProps: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: citiesApi.fetch.bind(citiesApi),
            params: ({ entity, props }) => {
                const countryCode = entity.getIn(['address', 'countryCode']);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'address.state',
        supportsOnBlur: true,
        label: 'transporterForm.state',
        placeholder: 'transporterForm.statePlaceholder',
        halfWidth: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.pincode',
        supportsOnBlur: true,
        label: 'transporterForm.pincode',
        placeholder: 'transporterForm.pincodePlaceholder',
        type: FieldTypeEnum.Pincode,
        halfWidth: true,
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        extraProps: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: pincodesApi.fetch.bind(pincodesApi),
            params: ({ entity, props }) => {
                const countryCode = entity.getIn(['address', 'countryCode']);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'address.countryCode',
        dependentAccessors: ['address.city', 'address.pincode'],
        label: 'transporterForm.country',
        type: FieldTypeEnum.Select,
        required: true,
        options: CountryHelper.getCountryCodeOptions(),
        placeholder: 'transporterForm.countryPlaceholder',
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        },
        extraProps: ({ field, props }) => {
            return {
                isSearchable: true,
                value: field.value || props.defaultCountryCode
            };
        }
    },
    {
        accessor: 'emailAddress.emailAddress',
        label: 'transporterForm.emailAddress',
        placeholder: 'transporterForm.emailAddressPlaceholder',
        supportsOnBlur: true,
        onChangeHandler: null,
        onBlurHandler: ({ field, value, entity }) => {
            return entity.setIn(field.absoluteAccessor, value || null);
        },
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'phoneNumber.phoneNumber',
        label: 'transporterForm.phoneNumber',
        placeholder: 'transporterForm.phoneNumber',
        type: FieldTypeEnum.Phone
    },
    {
        accessor: 'additionalPhoneNumbers',
        label: 'transporterForm.additionalPhoneNumbers',
        placeholder: 'transporterForm.additionalPhoneNumbers',
        type: FieldTypeEnum.Phone,
        extraProps: ({ field }) => {
            return {
                value: field.value?.toArray()[0]?.phoneNumber
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(
                field.absoluteAccessor,
                Immutable.List([PhoneNumber.fromJS({ phoneNumber: value })])
            );
        }
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'transporterSettings'
            };
        }
    },
    {
        accessor: 'documents',
        component: EntityDocumentUploadList,
        label: () => '',
        extraProps: ({ field, entity, t }) => {
            return {
                currentEntitySettings: 'transporterSettings',
                documents: entity.getIn(field.absoluteAccessor),
                t
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value);
        }
    }
];

const TRANSPORTER_INITIAL_FETCH_FIELDS = ['address.city', 'address.pincode'];

const SingleTransporterEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterTransporter,
    MasterActions: TransporterActions,
    EntityFetchAPI: transporterApi,
    INITIAL_FETCH_FIELDS: TRANSPORTER_INITIAL_FETCH_FIELDS,
    FIELDS: TRANSPORTER_FIELDS,
    extraApiParams: {
        include: IncludeTypeEnums.Contact
    },
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'transporterId',
        ENTITY_SETTINGS_NAME: 'transporterSettings'
    },
    mapEntityStateToProps: ({ state, props, baseProps }) => {
        const countryISOCode = ClientSelectors.getCountryISOCodeFromLocaleSettings(
            state,
            props
        );
        const teams = teamsSelector(state, props);
        let defaultCountryCode = null;
        if (baseProps.localeSettings) {
            defaultCountryCode = baseProps.localeSettings.countryCode;
        }

        return {
            teams,
            defaultCountryISOCode: countryISOCode,
            defaultCountryCode
        };
    }
});

export default SingleTransporterEditContainer;
